import { ARENA } from '../../config/constants.js';

const HEAL_RADIUS = 70;
const HEAL_RATE = 6; // HP/sec applied to every other alive minion within HEAL_RADIUS
const CONTACT_COOLDOWN_DURATION = 0.5;
const HIT_FLASH_DURATION = 0.08;

/**
 * Low-HP minion that continuously heals every other alive minion within
 * HEAL_RADIUS while it lives — a soft priority target: trivial to kill in
 * isolation, but costly to ignore in a crowd since it undoes chip damage on
 * everything near it. Needs visibility into its wave's other minions, which
 * no other minion type requires — MinionWaveSystem.update passes the full
 * `combat.minions` array as a 4th `allMinions` arg to every minion's
 * update() for this reason; every other type just ignores the extra param.
 */
export class Leech {
  constructor(eventBus, { x, y, radius = 5, hp, moveSpeed, contactDamage }) {
    this.eventBus = eventBus;
    this.type = 'leech';
    this.x = x;
    this.y = y;
    this.radius = radius;
    this.maxHp = hp;
    this.hp = hp;
    this.moveSpeed = moveSpeed;
    this.contactDamage = contactDamage;
    this.isDead = false;
    this._contactCooldown = 0;
    this.hitFlashTimer = 0;
  }

  update(dt, player, _bulletPool, allMinions) {
    this.hitFlashTimer = Math.max(0, this.hitFlashTimer - dt);

    const dx = player.x - this.x;
    const dy = player.y - this.y;
    const dist = Math.hypot(dx, dy) || 1;
    this.x += (dx / dist) * this.moveSpeed * dt;
    this.y += (dy / dist) * this.moveSpeed * dt;

    this._clampToArena();

    if (allMinions) {
      for (const other of allMinions) {
        if (other === this || other.isDead || other.hp === undefined || other.maxHp === undefined) continue;
        const healDist = Math.hypot(other.x - this.x, other.y - this.y);
        if (healDist <= HEAL_RADIUS) {
          other.hp = Math.min(other.maxHp, other.hp + HEAL_RATE * dt);
        }
      }
    }

    this._contactCooldown = Math.max(0, this._contactCooldown - dt);
    if (this._contactCooldown <= 0 && dist < this.radius + player.radius) {
      player.takeDamage(this.contactDamage, this.type);
      this._contactCooldown = CONTACT_COOLDOWN_DURATION;
    }
  }

  _clampToArena() {
    this.x = Math.min(ARENA.x + ARENA.width - this.radius, Math.max(ARENA.x + this.radius, this.x));
    this.y = Math.min(ARENA.y + ARENA.height - this.radius, Math.max(ARENA.y + this.radius, this.y));
  }

  takeDamage(amount) {
    if (this.isDead) return;
    const hpBefore = this.hp;
    this.hp = Math.max(0, this.hp - amount);
    const appliedDamage = hpBefore - this.hp;
    this.hitFlashTimer = HIT_FLASH_DURATION;
    this.eventBus.emit('minion:damaged', { amount: appliedDamage, x: this.x, y: this.y, minion: this });
    if (this.hp <= 0) {
      this.isDead = true;
      this.eventBus.emit('minion:died', { x: this.x, y: this.y, minion: this });
    }
  }
}
